import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import { LoadingScreen } from '@/components/ui/LoadingSpinner';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Logo from '@/components/ui/Logo';

const LoginPage: React.FC = () => {
  // Local loading state for button only; global auth loading comes from context
  const [btnLoading, setBtnLoading] = useState(false);
  const [error, setError] = useState('');

  const { googleAuth, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      const params = new URLSearchParams(window.location.search);
      const next = params.get('next') || '/cases';
      navigate(next, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleAuth = async () => {
    try {
      setBtnLoading(true);
      setError('');
      await googleAuth();
    } catch (err) {
      setError('Google authentication failed');
    } finally {
      setBtnLoading(false);
    }
  };

  // Prevent login form flicker after OAuth redirect while token is being exchanged
  const searchParams = new URLSearchParams(window.location.search);
  const oauthProcessing =
    searchParams.get('auth') === 'success' && searchParams.get('token');

  // Show loader by default while auth bootstrap or OAuth exchange in progress
  if (isLoading || oauthProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <LoadingScreen message="Checking session..." />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Login - ImmStack</title>
      </Helmet>
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Logo size="xl" />
            </div>
          </div>

          <Card padding="md" shadow="md">
            <div className="text-center">
              <h2 className="text-lg font-medium text-gray-600 mb-4">
                Sign in to ImmStack
              </h2>
            </div>
            <div className="space-y-6">
              {error && (
                <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                  <p className="text-sm text-error-700">{error}</p>
                </div>
              )}

              <Button
                type="button"
                onClick={handleGoogleAuth}
                loading={btnLoading}
                disabled={btnLoading}
                className="w-full flex items-center justify-center space-x-3"
                size="lg"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Continue with Google</span>
              </Button>

              <div className="text-center">
                <p className="text-xs text-neutral-500 leading-relaxed">
                  By continuing, you agree to our{' '}
                  <a
                    href="https://immstack.ai/legal/terms_of_service.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 text-neutral-600 hover:text-neutral-900 transition-colors"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://immstack.ai/legal/privacy_notice.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 text-neutral-600 hover:text-neutral-900 transition-colors"
                  >
                    Privacy Notice
                  </a>
                  .
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
